import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ChevronRight,
  ChevronDown,
  Search,
  ArrowLeft,
  Sun,
  Moon,
  ArrowUp,
  ExternalLink,
  Printer,
  LayoutDashboard,
  BookOpen,
  ClipboardCheck,
  Award,
  FileCheck,
  Map,
  Route,
  Shield,
  BarChart3,
  Trophy,
  Star,
  Library,
  Settings,
  Building2,
  LogIn,
  Bell,
  Sparkles,
  BookOpenCheck,
  GraduationCap,
  MessageSquare,
  UserCircle,
  Bot,
  Rocket,
  type LucideIcon,
} from "lucide-react";
import { moduleDocsData, moduleCategories, moduleRoutes } from "./docsData";
import { ADMIN_DASHBOARD } from "@/constants/constant-routes";
import { overviewSections, overviewGroups } from "./overviewDocsData";
import type { OverviewSection } from "./overviewDocsData";

type ActiveTab = "overview" | "modules";

const categoryIconMap: Record<string, LucideIcon> = {
  "layout-dashboard": LayoutDashboard,
  "book-open": BookOpen,
  "clipboard-check": ClipboardCheck,
  "award": Award,
  "file-check": FileCheck,
  "map": Map,
  "route": Route,
  "shield": Shield,
  "bar-chart-3": BarChart3,
  "trophy": Trophy,
  "star": Star,
  "library": Library,
  "settings": Settings,
  "building-2": Building2,
  "log-in": LogIn,
  "bell": Bell,
};

const overviewGroupIconMap: Record<string, LucideIcon> = {
  "Getting Started": Rocket,
  "Courses & Learning": GraduationCap,
  "Certifications & Exams": Award,
  "Learning Paths": Map,
  "AI Study Assistant": Bot,
  "Achievements & Gamification": Trophy,
  "Communication & Support": MessageSquare,
  "My Profile & Settings": UserCircle,
};

const SkillzenDocs: React.FC = () => {
  const { moduleType } = useParams<{ moduleType: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const isOverviewSection = moduleType ? overviewSections.some((s) => s.id === moduleType) : false;
  const [activeTab, setActiveTab] = useState<ActiveTab>(isOverviewSection ? "overview" : "modules");
  const [activeSection, setActiveSection] = useState(isOverviewSection ? moduleType! : overviewSections[0]?.id || "");
  const overviewScrollRef = useRef<HTMLDivElement>(null);
  const [tocItems, setTocItems] = useState<Array<{ id: string; label: string }>>([]);
  const [activeTocId, setActiveTocId] = useState("doc-overview");
  const [isLoading, setIsLoading] = useState(true);
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("sz-docs-theme") === "dark"; } catch { return false; }
  });
  const [readingProgress, setReadingProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const printMenuRef = useRef<HTMLDivElement>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [selectedOverviewGroup, setSelectedOverviewGroup] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    // Auto-expand the category containing the active module
    if (moduleType) {
      const cat = moduleCategories.find((c) => c.modules.includes(moduleType));
      return new Set(cat ? [cat.name] : []);
    }
    return new Set();
  });

  // Handle URL-based navigation for both admin modules and overview sections
  useEffect(() => {
    if (moduleType) {
      const overviewMatch = overviewSections.find((s) => s.id === moduleType);
      if (overviewMatch) {
        // It's a learner guide section
        setActiveTab("overview");
        setActiveSection(moduleType);
        setSelectedOverviewGroup(null);
      } else {
        // It's an admin module
        setActiveTab("modules");
        setSelectedOverviewGroup(null);
        const cat = moduleCategories.find((c) => c.modules.includes(moduleType));
        if (cat && !expandedCategories.has(cat.name)) {
          setExpandedCategories((prev) => new Set([...prev, cat.name]));
        }
      }
    }
  }, [moduleType]);

  // Auto-expand selected category in sidebar when viewing a category page
  useEffect(() => {
    if (selectedCategoryName && !expandedCategories.has(selectedCategoryName)) {
      setExpandedCategories((prev) => new Set([...prev, selectedCategoryName]));
    }
  }, [selectedCategoryName]);

  // Auto-expand user guide group containing active section or selected group
  useEffect(() => {
    if (activeTab === "overview" && activeSection) {
      const section = overviewSections.find((s) => s.id === activeSection);
      if (section?.group && !expandedCategories.has(section.group)) {
        setExpandedCategories((prev) => new Set([...prev, section.group]));
      }
    }
  }, [activeSection, activeTab]);

  useEffect(() => {
    if (selectedOverviewGroup && !expandedCategories.has(selectedOverviewGroup)) {
      setExpandedCategories((prev) => new Set([...prev, selectedOverviewGroup]));
    }
  }, [selectedOverviewGroup]);

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Persist theme preference
  useEffect(() => {
    try { localStorage.setItem("sz-docs-theme", isDark ? "dark" : "light"); } catch { /* noop */ }
  }, [isDark]);

  // Keyboard shortcut: Ctrl+D to toggle dark mode
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "d") {
        e.preventDefault();
        setIsDark((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Reading progress bar + back-to-top visibility
  const handleScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const progress = scrollHeight - clientHeight > 0 ? (scrollTop / (scrollHeight - clientHeight)) * 100 : 0;
    setReadingProgress(progress);
    setShowBackToTop(scrollTop > 400);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const currentDoc = moduleType ? moduleDocsData[moduleType] : undefined;

  // Current overview section for single-section rendering (like admin side)
  const currentOverviewSection = useMemo(() => {
    if (activeTab !== "overview" || !moduleType) return null;
    return overviewSections.find((s) => s.id === moduleType) || null;
  }, [activeTab, moduleType]);

  const currentOverviewIndex = moduleType ? overviewSections.findIndex((s) => s.id === moduleType) : -1;
  const prevOverviewSection = currentOverviewIndex > 0 ? overviewSections[currentOverviewIndex - 1] : null;
  const nextOverviewSection = currentOverviewIndex < overviewSections.length - 1 ? overviewSections[currentOverviewIndex + 1] : null;

  // Current overview group for breadcrumb
  const currentOverviewGroup = useMemo(() => {
    if (!currentOverviewSection) return null;
    return currentOverviewSection.group || null;
  }, [currentOverviewSection]);

  const currentCategory = useMemo(() => {
    if (!moduleType) return null;
    return moduleCategories.find((cat) => cat.modules.includes(moduleType)) || null;
  }, [moduleType]);

  const relatedModules = useMemo(() => {
    if (!currentCategory || !moduleType) return [];
    return currentCategory.modules.filter((n) => n !== moduleType);
  }, [currentCategory, moduleType]);

  const selectedCategory = useMemo(() => {
    if (!selectedCategoryName) return null;
    return moduleCategories.find((c) => c.name === selectedCategoryName) || null;
  }, [selectedCategoryName]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return moduleCategories;
    const q = searchQuery.toLowerCase();
    return moduleCategories
      .map((cat) => ({
        ...cat,
        modules: cat.modules.filter((key) => {
          const doc = moduleDocsData[key];
          return (
            key.toLowerCase().includes(q) ||
            doc?.title.toLowerCase().includes(q) ||
            doc?.description.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((cat) => cat.modules.length > 0);
  }, [searchQuery]);

  useEffect(() => {
    const scrollContainer = document.getElementById("docs-inner-viewport");
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [moduleType]);

  // Switch to correct tab when a module/section is selected via URL
  useEffect(() => {
    if (moduleType) {
      const isOverview = overviewSections.some((s) => s.id === moduleType);
      if (!isOverview) {
        setActiveTab("modules");
        setSelectedCategoryName(null);
      }
    }
  }, [moduleType]);

  // Scroll to top when overview section changes
  useEffect(() => {
    if (activeTab === "overview" && overviewScrollRef.current) {
      overviewScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [moduleType, activeTab]);

  // Extract TOC headings from rendered doc content (both admin modules and overview sections)
  useEffect(() => {
    const hasContent = activeTab === "modules" ? !!currentDoc : !!currentOverviewSection;
    if (!hasContent) {
      setTocItems([]);
      return;
    }

    const extractToc = () => {
      const container = activeTab === "overview"
        ? overviewScrollRef.current
        : document.getElementById("docs-inner-viewport");
      if (!container) return;

      const items: Array<{ id: string; label: string }> = [
        { id: "doc-overview", label: "Overview" },
      ];

      const sections = container.querySelectorAll(".sz-docs-content section");
      sections.forEach((section) => {
        const h2 = section.querySelector("h2");
        if (h2) {
          const text = h2.textContent || "";
          const id = `toc-${text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`;
          section.id = id;
          items.push({ id, label: text });
        }
      });

      const tipCallout = container.querySelector(".sz-docs-content .sz-callout-tip");
      if (tipCallout) {
        tipCallout.id = "toc-tips";
        items.push({ id: "toc-tips", label: "Tips" });
      }

      setTocItems(items);
      setActiveTocId("doc-overview");
    };

    requestAnimationFrame(extractToc);
  }, [moduleType, currentDoc, currentOverviewSection, activeTab]);

  // IntersectionObserver for TOC active tracking (both admin modules and overview sections)
  useEffect(() => {
    const hasContent = activeTab === "modules" ? !!currentDoc : !!currentOverviewSection;
    if (!tocItems.length || !hasContent) return;

    const container = activeTab === "overview"
      ? overviewScrollRef.current
      : document.getElementById("docs-inner-viewport");
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target.id) {
            setActiveTocId(entry.target.id);
          }
        }
      },
      { root: container, rootMargin: "-5% 0px -85% 0px", threshold: 0 }
    );

    tocItems.forEach(({ id }) => {
      const el = container.querySelector(`#${CSS.escape(id)}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [tocItems, activeTab, currentDoc, currentOverviewSection]);

  // Attach scroll listener for reading progress + back-to-top
  useEffect(() => {
    const overview = overviewScrollRef.current;
    const modules = document.getElementById("docs-inner-viewport");
    const target = activeTab === "overview" ? overview : modules;
    if (!target) return;
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, [activeTab, handleScroll, currentDoc]);

  const scrollToTop = useCallback(() => {
    const overview = overviewScrollRef.current;
    const modules = document.getElementById("docs-inner-viewport");
    const target = activeTab === "overview" ? overview : modules;
    target?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const printWindowRef = useRef<Window | null>(null);

  // Listen for messages from print window (image load progress + ready signal)
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.type === "pdf-progress") {
        setPdfLoading(`Loading images (${e.data.loaded} of ${e.data.total})...`);
      } else if (e.data.type === "pdf-ready") {
        setPdfLoading("Opening print preview...");
        const pw = printWindowRef.current;
        if (pw) {
          setTimeout(() => {
            pw.focus();
            pw.print();
            setPdfLoading(null);
          }, 300);
        } else {
          setPdfLoading(null);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Script injected into print windows to track image loading and notify parent
  const imageTrackingScript = `
    <script>
      (function() {
        var imgs = document.querySelectorAll('img');
        var total = imgs.length;
        if (total === 0) {
          window.opener && window.opener.postMessage({ type: 'pdf-ready' }, '*');
          return;
        }
        var loaded = 0;
        var done = false;
        function check() {
          if (done) return;
          loaded++;
          window.opener && window.opener.postMessage({ type: 'pdf-progress', loaded: loaded, total: total }, '*');
          if (loaded >= total) {
            done = true;
            window.opener && window.opener.postMessage({ type: 'pdf-ready' }, '*');
          }
        }
        imgs.forEach(function(img) {
          if (img.complete && img.naturalWidth > 0) {
            check();
          } else {
            img.addEventListener('load', check);
            img.addEventListener('error', check);
          }
        });
        // Fallback timeout: 30 seconds max wait
        setTimeout(function() {
          if (!done) {
            done = true;
            window.opener && window.opener.postMessage({ type: 'pdf-ready' }, '*');
          }
        }, 30000);
      })();
    </script>
  `;

  const handleDownloadPdf = useCallback(() => {
    const contentEl =
      activeTab === "overview"
        ? overviewScrollRef.current
        : document.getElementById("docs-inner-viewport");
    if (!contentEl) return;

    setPdfLoading("Preparing PDF...");

    const title =
      activeTab === "overview"
        ? currentOverviewSection?.title || "Skillzen User Guide"
        : currentDoc?.title || "Skillzen Docs";

    // Clone the content
    const clone = contentEl.cloneNode(true) as HTMLElement;

    // Remove "Open in Admin Panel" button
    clone.querySelectorAll("a").forEach((a) => {
      if (a.textContent?.includes("Open in Admin Panel")) {
        a.remove();
      }
    });

    // Remove breadcrumb navigation
    const breadcrumb = clone.querySelector("div");
    if (breadcrumb?.querySelector("svg") && (breadcrumb?.textContent?.includes("Admin Guide") || breadcrumb?.textContent?.includes("User Guide"))) {
      breadcrumb.remove();
    }

    // Remove Previous/Next footer navigation
    const footers = clone.querySelectorAll("footer");
    footers.forEach((footer) => footer.remove());

    // Add IDs to sections and build index
    const sections = clone.querySelectorAll(".sz-docs-content section, .sz-overview-content section");
    const indexItems: Array<{ id: string; label: string }> = [];
    sections.forEach((section, i) => {
      const h2 = section.querySelector("h2");
      if (h2) {
        const text = h2.textContent || "";
        const id = `pdf-section-${i}`;
        section.id = id;
        indexItems.push({ id, label: text });
      }
    });

    // Build index HTML
    let indexHtml = "";
    if (indexItems.length > 1) {
      indexHtml = `
        <div class="pdf-index" style="margin-bottom: 32px; padding: 24px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb;">
          <h2 style="font-size: 18px; font-weight: 700; margin: 0 0 16px 0; color: #374151;">Table of Contents</h2>
          <ol style="margin: 0; padding-left: 20px; list-style: decimal;">
            ${indexItems.map((item) => `
              <li style="margin-bottom: 6px;">
                <a href="#${item.id}" style="color: #696cff; text-decoration: none; font-size: 14px;">${item.label}</a>
              </li>
            `).join("")}
          </ol>
        </div>
      `;
    }

    const contentHtml = clone.innerHTML;

    // Copy all stylesheets from the current page
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => el.outerHTML)
      .join("\n");

    setPdfLoading("Loading images...");

    // Open a new window with original image URLs (no base64 — bypasses CORS)
    const printWindow = window.open("", "_blank");
    if (!printWindow) { setPdfLoading(null); return; }
    printWindowRef.current = printWindow;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        ${styles}
        <style>
          @page {
            margin: 15mm 10mm;
            size: A4;
          }
          body {
            margin: 0;
            padding: 20px 40px;
            background: ${isDark ? "#0f0f23" : "#ffffff"};
            color: ${isDark ? "#f3f4f6" : "#111827"};
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          .pdf-index a:hover { text-decoration: underline; }
          @media print {
            body { padding: 0; background: #fff !important; color: #111 !important; }
            img { max-width: 100% !important; }
            .pdf-index { break-after: page; background: #f9fafb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .pdf-index a { color: #696cff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body class="${isDark ? "sz-dark" : ""}">
        ${indexHtml}
        ${contentHtml}
        ${imageTrackingScript}
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [activeTab, currentDoc, currentOverviewSection, isDark, imageTrackingScript]);

  const handlePrintAllDocs = useCallback(() => {
    setShowPrintMenu(false);
    setPdfLoading("Gathering all documentation...");

    // Gather all docs content with IDs for index linking
    const allDocsHtml: string[] = [];
    const indexEntries: Array<{ id: string; title: string; group: string; type: "learner" | "admin" }> = [];
    let sectionCounter = 0;

    // Add overview (Learner Guide) sections
    overviewGroups.forEach((group) => {
      const groupSections = overviewSections.filter((s) => s.group === group);
      if (groupSections.length === 0) return;

      groupSections.forEach((s) => {
        const sectionId = `pdf-all-${sectionCounter++}`;
        indexEntries.push({ id: sectionId, title: s.title, group, type: "learner" });
        allDocsHtml.push(`
          <div id="${sectionId}" class="sz-docs-content" style="page-break-before: always;">
            <p style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #696cff; margin-bottom: 4px;">${group}</p>
            <h1 style="font-size: 22px; font-weight: 700; margin-bottom: 16px;">${s.title}</h1>
            <div>${s.content}</div>
          </div>
        `);
      });
    });

    // Add admin module docs
    const allModuleKeys = moduleCategories.flatMap((c) => c.modules);
    allModuleKeys.forEach((key) => {
      const doc = moduleDocsData[key];
      if (!doc) return;
      const cat = moduleCategories.find((c) => c.modules.includes(key));
      const sectionId = `pdf-all-${sectionCounter++}`;
      indexEntries.push({ id: sectionId, title: doc.title, group: cat?.name || "Admin", type: "admin" });

      allDocsHtml.push(`
        <div id="${sectionId}" class="sz-docs-content" style="page-break-before: always;">
          <h1 style="font-size: 24px; font-weight: 700; margin-bottom: 8px;">${doc.title}</h1>
          ${doc.subtitle ? `<p style="font-size: 14px; color: #6b7280; margin-bottom: 16px;">${doc.subtitle}</p>` : ""}
          <div>${doc.content}</div>
        </div>
      `);
    });

    // Build the index page
    const learnerEntries = indexEntries.filter((e) => e.type === "learner");
    const adminEntries = indexEntries.filter((e) => e.type === "admin");

    const buildIndexGroup = (entries: typeof indexEntries, sectionIndex: number[]) => {
      const groups: Record<string, typeof indexEntries> = {};
      entries.forEach((e) => {
        if (!groups[e.group]) groups[e.group] = [];
        groups[e.group].push(e);
      });
      return Object.entries(groups).map(([group, items]) => `
        <div style="margin-bottom: 20px; padding: 16px 20px; background: #f8fafc; border-radius: 10px; border: 1px solid #e5e7eb;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <div style="width: 6px; height: 6px; border-radius: 50%; background: #696cff; flex-shrink: 0;"></div>
            <h3 style="font-size: 13px; font-weight: 700; color: #1f2937; margin: 0; letter-spacing: 0.02em;">${group}</h3>
            <span style="font-size: 10px; font-weight: 600; color: #9ca3af; background: #f1f5f9; padding: 2px 8px; border-radius: 10px; margin-left: auto;">${items.length}</span>
          </div>
          <div style="display: flex; flex-direction: column; gap: 0;">
            ${items.map((item) => {
              const num = sectionIndex[0]++;
              return `
              <a href="#${item.id}" style="display: flex; align-items: center; gap: 10px; padding: 7px 12px; text-decoration: none; border-radius: 6px; transition: background 0.15s;" onmouseover="this.style.background='#eef2ff'" onmouseout="this.style.background='transparent'">
                <span style="font-size: 11px; font-weight: 700; color: #9ca3af; min-width: 22px; text-align: right; font-variant-numeric: tabular-nums;">${String(num).padStart(2, '0')}</span>
                <span style="width: 1px; height: 14px; background: #e5e7eb;"></span>
                <span style="font-size: 13px; color: #4b5563; font-weight: 500;">${item.title}</span>
                <span style="margin-left: auto; font-size: 11px; color: #c7d2fe;">&#8250;</span>
              </a>`;
            }).join("")}
          </div>
        </div>
      `).join("");
    };

    const learnerCounter = [1];
    const adminCounter = [learnerEntries.length + 1];

    const indexHtml = `
      <div class="pdf-index" style="padding: 0; margin-bottom: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <!-- Cover Header -->
        <div style="text-align: center; padding: 56px 40px 40px; position: relative; overflow: hidden;">
          <div style="position: absolute; top: 0; left: 0; right: 0; height: 5px; background: linear-gradient(90deg, #696cff, #8b8eff, #a78bfa, #696cff); background-size: 200% 100%; animation: none;"></div>
          <div style="display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; background: #f0f0ff; border-radius: 20px; font-size: 11px; font-weight: 600; color: #696cff; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 20px; border: 1px solid #e0e0ff;">
            DOCUMENTATION
          </div>
          <h1 style="font-size: 38px; font-weight: 800; margin: 0 0 10px 0; color: #111827; letter-spacing: -0.02em; line-height: 1.1;">
            <span style="background: linear-gradient(135deg, #696cff, #8b8eff); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Skillzen</span> Documentation
          </h1>
          <p style="font-size: 15px; color: #6b7280; margin: 0 0 24px 0; font-weight: 400;">Complete platform documentation — Learner Guide & Admin Guide</p>
          <div style="display: flex; justify-content: center; gap: 32px;">
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: 800; color: #111827;">${learnerEntries.length}</div>
              <div style="font-size: 11px; font-weight: 500; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em;">Learner Docs</div>
            </div>
            <div style="width: 1px; background: #e5e7eb;"></div>
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: 800; color: #111827;">${adminEntries.length}</div>
              <div style="font-size: 11px; font-weight: 500; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em;">Admin Docs</div>
            </div>
            <div style="width: 1px; background: #e5e7eb;"></div>
            <div style="text-align: center;">
              <div style="font-size: 24px; font-weight: 800; color: #111827;">${indexEntries.length}</div>
              <div style="font-size: 11px; font-weight: 500; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em;">Total Pages</div>
            </div>
          </div>
        </div>

        <!-- Table of Contents -->
        <div style="padding: 32px 40px 40px; max-width: 800px; margin: 0 auto;">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 24px; padding-bottom: 14px; border-bottom: 2px solid #111827;">
            <h2 style="font-size: 20px; font-weight: 800; margin: 0; color: #111827; letter-spacing: -0.01em;">Table of Contents</h2>
          </div>

          ${learnerEntries.length > 0 ? `
            <div style="margin-bottom: 28px;">
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                <div style="display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; background: linear-gradient(135deg, #696cff, #8b8eff); border-radius: 8px;">
                  <span style="font-size: 13px; font-weight: 700; color: #fff; letter-spacing: 0.01em;">Learner Guide</span>
                </div>
                <div style="flex: 1; height: 1px; background: #e5e7eb;"></div>
                <span style="font-size: 11px; font-weight: 600; color: #9ca3af;">${learnerEntries.length} sections</span>
              </div>
              ${buildIndexGroup(learnerEntries, learnerCounter)}
            </div>
          ` : ""}

          ${adminEntries.length > 0 ? `
            <div>
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                <div style="display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; background: linear-gradient(135deg, #111827, #374151); border-radius: 8px;">
                  <span style="font-size: 13px; font-weight: 700; color: #fff; letter-spacing: 0.01em;">Admin Guide</span>
                </div>
                <div style="flex: 1; height: 1px; background: #e5e7eb;"></div>
                <span style="font-size: 11px; font-weight: 600; color: #9ca3af;">${adminEntries.length} sections</span>
              </div>
              ${buildIndexGroup(adminEntries, adminCounter)}
            </div>
          ` : ""}
        </div>
      </div>
    `;

    const allContentHtml = allDocsHtml.join("");

    // Copy all stylesheets from the current page
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => el.outerHTML)
      .join("\n");

    setPdfLoading("Loading images...");

    const printWindow = window.open("", "_blank");
    if (!printWindow) { setPdfLoading(null); return; }
    printWindowRef.current = printWindow;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Skillzen - All Documentation</title>
        ${styles}
        <style>
          @page {
            margin: 15mm 10mm;
            size: A4;
          }
          body {
            margin: 0;
            padding: 20px 40px;
            background: ${isDark ? "#0f0f23" : "#ffffff"};
            color: ${isDark ? "#f3f4f6" : "#111827"};
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          .sz-screenshot {
            margin: 1rem 0 1.25rem;
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid #e5e7eb;
            background: #f9fafb;
          }
          .sz-screenshot img {
            width: 100%;
            height: auto;
            display: block;
          }
          .pdf-index a:hover { text-decoration: underline; }
          .pdf-index a { transition: background 0.15s; }
          @media print {
            body { padding: 0; background: #fff !important; color: #111 !important; }
            img { max-width: 100% !important; }
            .sz-screenshot { break-inside: avoid; }
            .pdf-index { break-after: page; }
            .pdf-index a { color: #4b5563 !important; }
            .pdf-index * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .pdf-index div[style*="background: linear-gradient"] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .pdf-index div[style*="background: #f8fafc"] { background: #f8fafc !important; }
            .pdf-index div[style*="background: #f0f0ff"] { background: #f0f0ff !important; }
            .pdf-index div[style*="background: #f1f5f9"] { background: #f1f5f9 !important; }
            .pdf-index div[style*="height: 5px"] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body class="${isDark ? "sz-dark" : ""}">
        ${indexHtml}
        ${allContentHtml}
        ${imageTrackingScript}
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [isDark, imageTrackingScript]);

  // Close print menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target as Node)) {
        setShowPrintMenu(false);
      }
    };
    if (showPrintMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPrintMenu]);

  const flatModules = useMemo(() => {
    return moduleCategories.flatMap((c) => c.modules);
  }, []);

  const currentIndex = moduleType ? flatModules.indexOf(moduleType) : -1;
  const prevModule = currentIndex > 0 ? flatModules[currentIndex - 1] : null;
  const nextModule =
    currentIndex < flatModules.length - 1 ? flatModules[currentIndex + 1] : null;

  if (isLoading) {
    return (
      <div className={`h-screen flex flex-col items-center justify-center relative overflow-hidden ${isDark ? "bg-[#0f0f23]" : "bg-white"}`}>
        {/* Background ambient glow */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="sz-loader-glow sz-loader-glow-1" />
          <div className="sz-loader-glow sz-loader-glow-2" />
        </div>

        {/* Spinner */}
        <div className="relative z-10 flex flex-col items-center gap-8">
          <div className="sz-loader-ring">
            <div className="sz-loader-ring-inner" />
            <div className="sz-loader-ring-dot" />
          </div>

          {/* Brand + label */}
          <div className="flex flex-col items-center gap-2">
            <span className={`text-[18px] font-bold tracking-tight ${isDark ? "text-gray-100" : "text-gray-900"}`}>Skillzen</span>
            <div className="flex items-center gap-2">
              <span className="sz-loader-pulse-dot" />
              <span className="text-[13px] text-gray-400 font-medium tracking-wide">Loading documentation</span>
            </div>
          </div>
        </div>

        <style>{`
          .sz-loader-glow {
            position: absolute;
            border-radius: 50%;
            filter: blur(80px);
            opacity: 0.4;
            animation: sz-glow-drift 6s ease-in-out infinite;
          }
          .sz-loader-glow-1 {
            width: 300px; height: 300px;
            background: radial-gradient(circle, #696cff 0%, transparent 70%);
            top: 30%; left: 35%;
            animation-delay: 0s;
          }
          .sz-loader-glow-2 {
            width: 250px; height: 250px;
            background: radial-gradient(circle, #8b8eff 0%, transparent 70%);
            top: 45%; left: 55%;
            animation-delay: -3s;
          }
          @keyframes sz-glow-drift {
            0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
            50% { transform: translate(20px, -15px) scale(1.15); opacity: 0.5; }
          }

          .sz-loader-ring {
            width: 56px; height: 56px;
            position: relative;
            animation: sz-ring-spin 1.8s linear infinite;
          }
          .sz-loader-ring-inner {
            position: absolute; inset: 0;
            border-radius: 50%;
            border: 2.5px solid #f3f4f6;
            border-top-color: #696cff;
            border-right-color: #696cff;
          }
          .sz-loader-ring-dot {
            position: absolute;
            width: 8px; height: 8px;
            background: #696cff;
            border-radius: 50%;
            top: 0; left: 50%;
            transform: translateX(-50%);
            box-shadow: 0 0 12px 2px rgba(79, 70, 229, 0.4);
          }
          @keyframes sz-ring-spin {
            to { transform: rotate(360deg); }
          }

          .sz-loader-pulse-dot {
            width: 6px; height: 6px;
            background: #696cff;
            border-radius: 50%;
            animation: sz-pulse 1.4s ease-in-out infinite;
          }
          @keyframes sz-pulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={`h-screen font-sans overflow-hidden flex flex-col ${isDark ? "sz-dark bg-[#0f0f23] text-gray-100" : "bg-white text-gray-900"}`}>
      {/* ─── READING PROGRESS BAR ─── */}
      <div className="h-[2px] shrink-0 relative z-40" style={{ background: isDark ? "#1a1a2e" : "#f3f4f6" }}>
        <div
          className="h-full transition-[width] duration-150 ease-out"
          style={{ width: `${readingProgress}%`, background: "linear-gradient(90deg, #696cff, #8b8eff)" }}
        />
      </div>

      {/* ─── PDF LOADING OVERLAY ─── */}
      {pdfLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
          <div className={`flex flex-col items-center gap-4 px-8 py-6 rounded-xl shadow-2xl ${isDark ? "bg-[#1a1a35]" : "bg-white"}`}>
            <div className="w-10 h-10 border-3 border-gray-200 border-t-[#696cff] rounded-full animate-spin" style={{ borderWidth: "3px" }} />
            <p className={`text-[14px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>{pdfLoading}</p>
            <p className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>Please wait, do not close this tab</p>
          </div>
        </div>
      )}

      {/* ─── TOP NAVIGATION BAR ─── */}
      <header className={`h-[52px] flex items-center shrink-0 relative z-30 border-b ${isDark ? "bg-[#13132b] border-[#1e1e3a]" : "bg-white border-gray-200"}`}>
        <div className="flex items-center gap-6 w-full px-6">
          <div className="flex items-center mr-4 cursor-pointer" onClick={() => navigate(ADMIN_DASHBOARD)}>
            <img src={isDark ? '/logo/Skillzen Final Logo-02.png' : '/logo/Skillzen Final Logo-01.png'} alt="Skillzen" className="h-[34px] w-auto" />
          </div>
          <button
            onClick={() => { setActiveTab("modules"); setSelectedCategoryName(null); navigate("/docs"); }}
            className={`text-[13px] font-medium cursor-pointer h-[50px] flex items-center transition-colors border-b-2 ${
              activeTab === "modules"
                ? `${isDark ? "text-white" : "text-gray-900"} border-[#696cff]`
                : `${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-900"} border-transparent`
            }`}
          >
            Admin Guide
          </button>
          <button
            onClick={() => { setActiveTab("overview"); setSelectedOverviewGroup(null); navigate("/docs"); }}
            className={`text-[13px] font-medium cursor-pointer h-[50px] flex items-center transition-colors border-b-2 ${
              activeTab === "overview"
                ? `${isDark ? "text-white" : "text-gray-900"} border-[#696cff]`
                : `${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-900"} border-transparent`
            }`}
          >
            Learner Guide
          </button>
          <div className="ml-auto flex items-center gap-1">
            {/* <div className="relative" ref={printMenuRef}>
              <button
                onClick={() => setShowPrintMenu((prev) => !prev)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? "text-gray-400 hover:text-gray-200 hover:bg-[#1a1a35]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
              >
                <Printer className="w-4.5 h-4.5" />
                <span className="text-[12px] font-medium">Print</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showPrintMenu && (
                <div className={`absolute right-0 top-full mt-1 rounded-lg shadow-lg border z-50 min-w-[180px] py-1 ${isDark ? "bg-[#1a1a35] border-[#2a2a4a]" : "bg-white border-gray-200"}`}>
                  <button
                    onClick={() => { setShowPrintMenu(false); handleDownloadPdf(); }}
                    className={`w-full text-left px-4 py-2 text-[13px] transition-colors cursor-pointer ${isDark ? "text-gray-300 hover:bg-[#2a2a4a]" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    Print Current Page
                  </button>
                  <button
                    onClick={handlePrintAllDocs}
                    className={`w-full text-left px-4 py-2 text-[13px] transition-colors cursor-pointer ${isDark ? "text-gray-300 hover:bg-[#2a2a4a]" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    Print All Docs
                  </button>
                </div>
              )}
            </div> */}
            <button
              onClick={() => setIsDark((prev) => !prev)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? "text-gray-400 hover:text-gray-200 hover:bg-[#1a1a35]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
              title={isDark ? "Switch to light mode (Ctrl+D)" : "Switch to dark mode (Ctrl+D)"}
            >
              {isDark ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
              <span className="text-[12px] font-medium">{isDark ? "Light" : "Dark"}</span>
            </button>
          </div>
        </div>
      </header>

      {/* ─── MAIN CONTENT CONTAINER (FULL WIDTH) ─── */}
      <div className={`flex-1 flex overflow-hidden ${isDark ? "bg-[#0f0f23]" : "bg-white"}`}>
        <div className={`w-full flex h-full overflow-hidden ${isDark ? "bg-[#13132b]" : "bg-white"}`}>

          {/* ─── LEFT SIDEBAR ─── */}
          <aside className={`w-[256px] flex flex-col shrink-0 ${isDark ? "bg-[#13132b] border-r border-[#1e1e3a]" : "bg-white border-r border-stone-100"}`}>
            {activeTab === "modules" ? (
              <>
                <div className="px-3 pt-4 pb-2">
                  <h2 className={`text-[13px] font-bold mb-2 px-1 ${isDark ? "text-gray-200" : "text-gray-900"}`}>Admin Guide</h2>
                  <div className="relative">
                    <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-8 pr-3 py-[5px] text-[12px] rounded focus:outline-none transition-all ${isDark ? "bg-[#1a1a35] border border-[#2a2a4a] text-gray-200 focus:border-[#696cff] placeholder-gray-500" : "bg-stone-50 border border-stone-200 text-gray-700 focus:border-stone-300 placeholder-stone-400"}`}
                    />
                  </div>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-1 sz-docs-scrollbar">
                  {filteredCategories.map((category) => {
                    const isSingleModule = category.modules.length === 1;
                    const isExpanded = expandedCategories.has(category.name);
                    const hasActiveModule = moduleType ? category.modules.includes(moduleType) : false;
                    const isSelectedCategory = selectedCategoryName === category.name;
                    const isHighlighted = hasActiveModule || isSelectedCategory;
                    return (
                      <div key={category.name} className="mb-0">
                        <button
                          onClick={() => {
                            if (isSingleModule) {
                              navigate(`/docs/${category.modules[0]}`);
                            } else {
                              toggleCategory(category.name);
                            }
                          }}
                          className={`w-full flex items-center gap-1.5 px-2 py-[4px] rounded-md text-[13px] font-semibold transition-all duration-200 cursor-pointer ${
                            isHighlighted
                              ? isDark ? "text-[#696cff] bg-[#1a1a35]/50" : "text-[#696cff] bg-indigo-50/50"
                              : isDark ? "text-gray-400 hover:text-gray-200 hover:bg-[#1a1a35]" : "text-gray-700 hover:text-gray-900 hover:bg-stone-50"
                          }`}
                        >
                          {!isSingleModule ? (
                            <ChevronRight className={`w-3 h-3 shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""} ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                          ) : (
                            <span className="w-3 h-3 shrink-0" />
                          )}
                          <span className="truncate">{category.name}</span>
                        </button>
                        {!isSingleModule && (
                          <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                            <div className={`ml-1.5 pl-2 border-l ${isDark ? "border-[#1e1e3a]" : "border-stone-200/60"}`}>
                              {category.modules.map((key) => {
                                const doc = moduleDocsData[key];
                                if (!doc) return null;
                                const isActive = moduleType === key;
                                return (
                                  <button
                                    key={key}
                                    onClick={() => navigate(`/docs/${key}`)}
                                    className={`w-full text-left px-2 py-[3px] text-[12.5px] truncate transition-all duration-200 rounded ${
                                      isActive
                                        ? `text-[#696cff] font-semibold ${isDark ? "bg-[#1a1a35]" : "bg-stone-50"}`
                                        : isDark ? "text-gray-400 hover:text-gray-200 hover:bg-[#1a1a35]" : "text-gray-600 hover:text-gray-900 hover:bg-stone-50/50"
                                    }`}
                                    title={doc.title}
                                  >
                                    {doc.title}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </>
            ) : (
              <>
                <div className="px-3 pt-4 pb-2">
                  <h2 className={`text-[13px] font-bold mb-2 px-1 ${isDark ? "text-gray-200" : "text-gray-900"}`}>User Guide</h2>
                  <div className="relative">
                    <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-8 pr-3 py-[5px] text-[12px] rounded focus:outline-none transition-all ${isDark ? "bg-[#1a1a35] border border-[#2a2a4a] text-gray-200 focus:border-[#696cff] placeholder-gray-500" : "bg-stone-50 border border-stone-200 text-gray-700 focus:border-stone-300 placeholder-stone-400"}`}
                    />
                  </div>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-1 sz-docs-scrollbar">
                  {overviewGroups.map((groupName) => {
                    const groupSections = overviewSections.filter(
                      (s: OverviewSection) => s.group === groupName
                    );
                    if (groupSections.length === 0) return null;
                    const isExpanded = expandedCategories.has(groupName);
                    const hasActiveSection = groupSections.some((s) => activeSection === s.id);
                    return (
                      <div key={groupName} className="mb-0">
                        <button
                          onClick={() => toggleCategory(groupName)}
                          className={`w-full flex items-center gap-1.5 px-2 py-[4px] rounded-md text-[13px] font-semibold transition-all duration-200 cursor-pointer ${
                            hasActiveSection
                              ? isDark ? "text-[#696cff] bg-[#1a1a35]/50" : "text-[#696cff] bg-indigo-50/50"
                              : isDark ? "text-gray-400 hover:text-gray-200 hover:bg-[#1a1a35]" : "text-gray-700 hover:text-gray-900 hover:bg-stone-50"
                          }`}
                        >
                          <ChevronRight className={`w-3 h-3 shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""} ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                          <span className="truncate">{groupName}</span>
                        </button>
                        <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                          <div className={`ml-1.5 pl-2 border-l ${isDark ? "border-[#1e1e3a]" : "border-stone-200/60"}`}>
                            {groupSections.map((section: OverviewSection) => {
                              const isActive = activeSection === section.id;
                              return (
                                <button
                                  key={section.id}
                                  onClick={() => {
                                    navigate(`/docs/${section.id}`);
                                  }}
                                  className={`w-full text-left px-2 py-[3px] text-[12.5px] truncate transition-all duration-200 rounded ${
                                    isActive
                                      ? `text-[#696cff] font-semibold ${isDark ? "bg-[#1a1a35]" : "bg-stone-50"}`
                                      : isDark ? "text-gray-400 hover:text-gray-200 hover:bg-[#1a1a35]" : "text-gray-600 hover:text-gray-900 hover:bg-stone-50/50"
                                  }`}
                                  title={section.title}
                                >
                                  {section.title}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </nav>
              </>
            )}

            <div className={`p-4 border-t ${isDark ? "border-[#1e1e3a]" : "border-stone-50"}`}>
              <button
                onClick={() => navigate("/")}
                className={`flex items-center gap-2 text-[12px] transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-700"}`}
              >
                <ArrowLeft className="w-3 h-3" />
                Back to Dashboard
              </button>
            </div>
          </aside>

          {/* ─── MAIN CONTENT PANEL ─── */}
          <main className="flex-1 flex overflow-hidden">
            {activeTab === "overview" && currentOverviewSection ? (
              /* ── Overview Content: Single Section (like admin side) ── */
              <div className="flex-1 flex min-w-0 h-full">
                <div ref={overviewScrollRef} className={`flex-1 overflow-y-auto px-10 py-8 sz-docs-scrollbar scroll-smooth ${isDark ? "bg-[#0f0f23]" : ""}`}>
                  <div className={`flex items-center gap-1.5 text-[13px] mb-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    <span className={`cursor-pointer ${isDark ? "hover:text-gray-300" : "hover:text-gray-600"}`} onClick={() => navigate("/docs")}>User Guide</span>
                    {currentOverviewGroup && (
                      <>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span className={`cursor-pointer ${isDark ? "hover:text-gray-300" : "hover:text-gray-600"}`} onClick={() => { setSelectedOverviewGroup(currentOverviewGroup); navigate("/docs"); }}>{currentOverviewGroup}</span>
                      </>
                    )}
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className={`font-normal ${isDark ? "text-gray-200" : "text-gray-800"}`}>{currentOverviewSection.title}</span>
                  </div>

                  <h1 id="doc-overview" className={`text-[34px] font-bold tracking-tight leading-tight mb-8 scroll-mt-6 ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                    {currentOverviewSection.title}
                  </h1>

                  <div className={`mb-8 h-px ${isDark ? "bg-[#1e1e3a]" : "bg-stone-100"}`} />

                  <div className="sz-docs-content sz-overview-content">
                    <div dangerouslySetInnerHTML={{ __html: currentOverviewSection.content }} />
                  </div>

                  <footer className={`mt-20 pt-10 border-t flex items-stretch gap-4 pb-20 ${isDark ? "border-[#1e1e3a]" : "border-stone-100"}`}>
                    {prevOverviewSection ? (
                      <button
                        onClick={() => navigate(`/docs/${prevOverviewSection.id}`)}
                        className={`flex-1 group text-left p-4 rounded-lg border transition-all duration-200 ${isDark ? "border-[#1e1e3a] hover:border-[#696cff]/40 bg-[#13132b] hover:bg-[#1a1a35]" : "border-stone-200 hover:border-[#696cff]/40 bg-white hover:bg-stone-50/50"}`}
                      >
                        <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Previous</div>
                        <div className={`text-[14px] font-medium group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          &larr; {prevOverviewSection.title}
                        </div>
                      </button>
                    ) : <div className="flex-1" />}
                    {nextOverviewSection ? (
                      <button
                        onClick={() => navigate(`/docs/${nextOverviewSection.id}`)}
                        className={`flex-1 group text-right p-4 rounded-lg border transition-all duration-200 ${isDark ? "border-[#1e1e3a] hover:border-[#696cff]/40 bg-[#13132b] hover:bg-[#1a1a35]" : "border-stone-200 hover:border-[#696cff]/40 bg-white hover:bg-stone-50/50"}`}
                      >
                        <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Next</div>
                        <div className={`text-[14px] font-medium group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {nextOverviewSection.title} &rarr;
                        </div>
                      </button>
                    ) : <div className="flex-1" />}
                  </footer>
                </div>

                {/* ─── Right Sidebar: Dynamic TOC ─── */}
                <aside className={`w-[220px] hidden xl:block py-8 overflow-y-auto shrink-0 border-l sz-docs-scrollbar ${isDark ? "border-[#1e1e3a] bg-[#13132b]" : "border-stone-100"}`}>
                  {tocItems.length > 0 && (
                    <div className="px-5">
                      <h3 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-[0.1em] mb-3">On this page</h3>
                      <nav className="space-y-0.5">
                        {tocItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              const container = overviewScrollRef.current;
                              const el = container?.querySelector(`#${CSS.escape(item.id)}`);
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                              setActiveTocId(item.id);
                            }}
                            className={`block w-full text-left py-1 text-[13px] transition-colors ${
                              activeTocId === item.id
                                ? "text-[#696cff] font-semibold"
                                : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </nav>
                    </div>
                  )}
                </aside>
              </div>
            ) : activeTab === "overview" && !currentOverviewSection && selectedOverviewGroup ? (
              /* ── Learner Guide: Group Detail View with Section Cards ── */
              (() => {
                const groupSections = overviewSections.filter((s) => s.group === selectedOverviewGroup);
                const overviewGroupColorMap: Record<string, string> = {
                  "Getting Started": "indigo",
                  "Courses & Learning": "blue",
                  "Certifications & Exams": "violet",
                  "Learning Paths": "emerald",
                  "AI Study Assistant": "amber",
                  "Achievements & Gamification": "rose",
                  "Communication & Support": "slate",
                  "My Profile & Settings": "slate",
                };
                const groupColor = overviewGroupColorMap[selectedOverviewGroup] || "slate";
                const borderColorMap: Record<string, string> = {
                  blue: isDark ? "border-blue-800/40 hover:border-blue-500/60" : "border-blue-100 hover:border-blue-300",
                  violet: isDark ? "border-violet-800/40 hover:border-violet-500/60" : "border-violet-100 hover:border-violet-300",
                  indigo: isDark ? "border-indigo-800/40 hover:border-indigo-500/60" : "border-indigo-100 hover:border-indigo-300",
                  amber: isDark ? "border-amber-800/40 hover:border-amber-500/60" : "border-amber-100 hover:border-amber-300",
                  emerald: isDark ? "border-emerald-800/40 hover:border-emerald-500/60" : "border-emerald-100 hover:border-emerald-300",
                  rose: isDark ? "border-rose-800/40 hover:border-rose-500/60" : "border-rose-100 hover:border-rose-300",
                  slate: isDark ? "border-slate-700/40 hover:border-slate-500/60" : "border-slate-200 hover:border-slate-300",
                };
                const badgeColorMap: Record<string, string> = {
                  blue: isDark ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-600",
                  violet: isDark ? "bg-violet-900/30 text-violet-400" : "bg-violet-50 text-violet-600",
                  indigo: isDark ? "bg-indigo-900/30 text-indigo-400" : "bg-indigo-50 text-indigo-600",
                  amber: isDark ? "bg-amber-900/30 text-amber-400" : "bg-amber-50 text-amber-600",
                  emerald: isDark ? "bg-emerald-900/30 text-emerald-400" : "bg-emerald-50 text-emerald-600",
                  rose: isDark ? "bg-rose-900/30 text-rose-400" : "bg-rose-50 text-rose-600",
                  slate: isDark ? "bg-slate-800/40 text-slate-400" : "bg-slate-50 text-slate-600",
                };
                const borderClass = borderColorMap[groupColor] || borderColorMap.slate;
                const badgeClass = badgeColorMap[groupColor] || badgeColorMap.slate;

                return (
                  <div ref={overviewScrollRef} className={`flex-1 overflow-y-auto px-10 py-8 sz-docs-scrollbar scroll-smooth ${isDark ? "bg-[#0f0f23]" : ""}`}>
                    <div className="max-w-[1100px] mx-auto">
                      {/* Breadcrumb */}
                      <div className={`flex items-center gap-1.5 text-[13px] mb-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        <span className={`cursor-pointer ${isDark ? "hover:text-gray-300" : "hover:text-gray-600"}`} onClick={() => setSelectedOverviewGroup(null)}>User Guide</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span className={`font-normal ${isDark ? "text-gray-200" : "text-gray-800"}`}>{selectedOverviewGroup}</span>
                      </div>

                      <h1 className={`text-[34px] font-bold tracking-tight leading-tight mb-2 ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                        {selectedOverviewGroup}
                      </h1>
                      <p className={`text-[16px] leading-relaxed mb-10 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {groupSections.map((s) => s.title).join(", ")}
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        {groupSections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => navigate(`/docs/${section.id}`)}
                            className={`text-left p-5 rounded-lg border-2 transition-all duration-200 cursor-pointer group ${borderClass} ${isDark ? "bg-[#13132b] hover:bg-[#1a1a35]" : "bg-white hover:bg-gray-50/50"}`}
                          >
                            <div className="flex items-center gap-2.5 mb-2">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${badgeClass}`}>
                                {selectedOverviewGroup}
                              </span>
                            </div>
                            <div className={`text-[15px] font-semibold mb-1 group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                              {section.title}
                            </div>
                            <div className={`text-[11px] font-medium mt-3 ${isDark ? "text-[#696cff]/70" : "text-[#696cff]/60"}`}>
                              View documentation &rarr;
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className="pb-20" />
                    </div>
                  </div>
                );
              })()
            ) : activeTab === "overview" && !currentOverviewSection ? (
              /* ── Learner Guide: Landing Page with Group Cards ── */
              <div ref={overviewScrollRef} className={`flex-1 overflow-y-auto sz-docs-scrollbar scroll-smooth ${isDark ? "bg-[#0f0f23]" : "bg-gray-50/30"}`}>
                {/* Hero Section */}
                <div className={`relative overflow-hidden ${isDark ? "bg-gradient-to-br from-[#13132b] via-[#1a1a40] to-[#0f0f23]" : "bg-gradient-to-br from-white via-violet-50/30 to-white"}`}>
                  <div className="sz-landing-mesh-1" />
                  <div className="sz-landing-mesh-2" />
                  <div className="relative z-10 max-w-[1100px] mx-auto px-10 pt-12 pb-10">
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide ${isDark ? "bg-violet-500/15 text-violet-400 border border-violet-500/20" : "bg-violet-500/8 text-violet-600 border border-violet-500/15"}`}>
                        <BookOpenCheck className="w-3 h-3" />
                        LEARNER DOCUMENTATION
                      </div>
                    </div>
                    <h1 className={`text-[42px] font-extrabold tracking-tight leading-[1.1] mb-3 ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                      <span className="sz-landing-gradient-text">Skillzen</span> User Guide
                    </h1>
                    <p className={`text-[17px] leading-relaxed max-w-[600px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Everything you need to know as a learner — find courses, earn certifications, track your progress, and get the most out of the platform.
                    </p>

                    {/* Stats Bar */}
                    <div className={`flex items-center gap-6 mt-8 pt-6 border-t ${isDark ? "border-[#1e1e3a]" : "border-gray-200/60"}`}>
                      {[
                        { label: "Topics", value: overviewGroups.length },
                        { label: "Sections", value: overviewSections.length },
                        { label: "Coverage", value: "Complete" },
                      ].map((stat) => (
                        <div key={stat.label} className="flex items-center gap-2.5">
                          <span className={`text-[20px] font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>{stat.value}</span>
                          <span className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}>{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Group Cards Grid */}
                <div className="max-w-[1100px] mx-auto px-10 py-8">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {overviewGroups.map((groupName) => {
                      const groupSections = overviewSections.filter((s) => s.group === groupName);
                      if (groupSections.length === 0) return null;
                      const IconComp = overviewGroupIconMap[groupName] || BookOpen;

                      const groupColorMap: Record<string, string> = {
                        "Getting Started": "indigo",
                        "Courses & Learning": "blue",
                        "Certifications & Exams": "violet",
                        "Learning Paths": "emerald",
                        "AI Study Assistant": "amber",
                        "Achievements & Gamification": "rose",
                        "Communication & Support": "slate",
                        "My Profile & Settings": "slate",
                      };
                      const color = groupColorMap[groupName] || "slate";
                      const iconBgMap: Record<string, string> = {
                        blue: isDark ? "bg-blue-500/10 text-blue-400" : "bg-blue-50 text-blue-500",
                        violet: isDark ? "bg-violet-500/10 text-violet-400" : "bg-violet-50 text-violet-500",
                        indigo: isDark ? "bg-indigo-500/10 text-indigo-400" : "bg-indigo-50 text-indigo-500",
                        amber: isDark ? "bg-amber-500/10 text-amber-400" : "bg-amber-50 text-amber-500",
                        emerald: isDark ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-50 text-emerald-500",
                        rose: isDark ? "bg-rose-500/10 text-rose-400" : "bg-rose-50 text-rose-500",
                        slate: isDark ? "bg-slate-500/10 text-slate-400" : "bg-slate-100 text-slate-500",
                      };
                      const iconBg = iconBgMap[color] || iconBgMap.slate;

                      return (
                        <button
                          key={groupName}
                          onClick={() => {
                            if (groupSections.length === 1) {
                              navigate(`/docs/${groupSections[0].id}`);
                            } else {
                              setSelectedOverviewGroup(groupName);
                            }
                          }}
                          className={`sz-landing-card text-left p-5 rounded-xl transition-all duration-300 cursor-pointer group border ${
                            isDark
                              ? "bg-[#13132b]/80 hover:bg-[#1a1a40] border-[#1e1e3a] hover:border-[#696cff]/30"
                              : "bg-white hover:bg-white border-gray-200/80 hover:border-[#696cff]/30 hover:shadow-lg hover:shadow-indigo-500/5"
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 ${iconBg}`}>
                              <IconComp className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className={`text-[14px] font-semibold mb-0.5 group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                                {groupName}
                              </div>
                              <div className={`text-[12px] leading-relaxed line-clamp-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                                {groupSections.map((s) => s.title).join(", ")}
                              </div>
                              <div className={`flex items-center gap-1.5 mt-2.5 text-[11px] font-medium ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isDark ? "bg-[#1e1e3a] text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                                  {groupSections.length}
                                </span>
                                <span>{groupSections.length === 1 ? "section" : "sections"}</span>
                                <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200" />
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="pb-20" />
                </div>
              </div>
            ) : currentDoc ? (
              /* ── Feature Modules: Module Doc ── */
              <div className="flex-1 flex min-w-0 h-full">
                <div id="docs-inner-viewport" className={`flex-1 overflow-y-auto px-10 py-8 sz-docs-scrollbar scroll-smooth ${isDark ? "bg-[#0f0f23]" : ""}`}>
                  <div className={`flex items-center gap-1.5 text-[13px] mb-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    <span className={`cursor-pointer ${isDark ? "hover:text-gray-300" : "hover:text-gray-600"}`} onClick={() => { setSelectedCategoryName(null); navigate("/docs"); }}>Admin Guide</span>
                    {currentCategory && currentCategory.modules.length > 1 && (
                      <>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span className={`cursor-pointer ${isDark ? "hover:text-gray-300" : "hover:text-gray-600"}`} onClick={() => { setSelectedCategoryName(currentCategory.name); navigate("/docs"); }}>{currentCategory.name}</span>
                      </>
                    )}
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className={`font-normal ${isDark ? "text-gray-200" : "text-gray-800"}`}>{currentDoc.title}</span>
                  </div>

                  <h1 id="doc-overview" className={`text-[34px] font-bold tracking-tight leading-tight mb-8 scroll-mt-6 ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                    {currentDoc.title}
                  </h1>

                  <div className={`mb-6 text-[16px] leading-relaxed max-w-[900px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {currentDoc.description}
                  </div>

                  {isAuthenticated && moduleType && moduleRoutes[moduleType] && (
                    <div className="mb-8">
                      <a
                        href={moduleRoutes[moduleType]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                          isDark
                            ? "bg-[#696cff]/10 text-[#696cff] border border-[#696cff]/20 hover:bg-[#696cff]/20 hover:border-[#696cff]/40"
                            : "bg-[#696cff]/5 text-[#696cff] border border-[#696cff]/15 hover:bg-[#696cff]/10 hover:border-[#696cff]/30"
                        }`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open in Admin Panel
                      </a>
                    </div>
                  )}

                  <div className={`mb-8 h-px ${isDark ? "bg-[#1e1e3a]" : "bg-stone-100"}`} />

                  <div className="sz-docs-content">
                    <div dangerouslySetInnerHTML={{ __html: currentDoc.content }} />
                  </div>

                  <footer className={`mt-20 pt-10 border-t flex items-stretch gap-4 pb-20 ${isDark ? "border-[#1e1e3a]" : "border-stone-100"}`}>
                    {prevModule ? (
                      <button
                        onClick={() => navigate(`/docs/${prevModule}`)}
                        className={`flex-1 group text-left p-4 rounded-lg border transition-all duration-200 ${isDark ? "border-[#1e1e3a] hover:border-[#696cff]/40 bg-[#13132b] hover:bg-[#1a1a35]" : "border-stone-200 hover:border-[#696cff]/40 bg-white hover:bg-stone-50/50"}`}
                      >
                        <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Previous</div>
                        <div className={`text-[14px] font-medium group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          &larr; {moduleDocsData[prevModule]?.title}
                        </div>
                      </button>
                    ) : <div className="flex-1" />}
                    {nextModule ? (
                      <button
                        onClick={() => navigate(`/docs/${nextModule}`)}
                        className={`flex-1 group text-right p-4 rounded-lg border transition-all duration-200 ${isDark ? "border-[#1e1e3a] hover:border-[#696cff]/40 bg-[#13132b] hover:bg-[#1a1a35]" : "border-stone-200 hover:border-[#696cff]/40 bg-white hover:bg-stone-50/50"}`}
                      >
                        <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Next</div>
                        <div className={`text-[14px] font-medium group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {moduleDocsData[nextModule]?.title} &rarr;
                        </div>
                      </button>
                    ) : <div className="flex-1" />}
                  </footer>
                </div>

                {/* ─── Right Sidebar: Dynamic TOC + Related ─── */}
                <aside className={`w-[220px] hidden xl:block py-8 overflow-y-auto shrink-0 border-l sz-docs-scrollbar ${isDark ? "border-[#1e1e3a] bg-[#13132b]" : "border-stone-100"}`}>
                  {/* On this page */}
                  {tocItems.length > 0 && (
                    <div className="px-5">
                      <h3 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-[0.1em] mb-3">On this page</h3>
                      <nav className="space-y-0.5">
                        {tocItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              const container = document.getElementById("docs-inner-viewport");
                              const el = container?.querySelector(`#${CSS.escape(item.id)}`);
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                              setActiveTocId(item.id);
                            }}
                            className={`block w-full text-left py-1 text-[13px] transition-colors ${
                              activeTocId === item.id
                                ? "text-[#696cff] font-semibold"
                                : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </nav>
                    </div>
                  )}

                  {/* Related modules in same category */}
                  {relatedModules.length > 0 && (
                    <div className={`px-5 mt-6 pt-6 border-t ${isDark ? "border-[#1e1e3a]" : "border-stone-100"}`}>
                      <h3 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-[0.1em] mb-3">
                        {currentCategory?.name || "Related"}
                      </h3>
                      <nav className="space-y-0.5">
                        {relatedModules.map((key) => {
                          const doc = moduleDocsData[key];
                          if (!doc) return null;
                          return (
                            <button
                              key={key}
                              onClick={() => navigate(`/docs/${key}`)}
                              className={`block w-full text-left py-1 text-[13px] hover:text-[#696cff] transition-colors ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {doc.title}
                            </button>
                          );
                        })}
                      </nav>
                    </div>
                  )}
                </aside>
              </div>
            ) : selectedCategory ? (
              /* ── Feature Modules: Category Detail View with Sub-Module Cards ── */
              <div id="docs-inner-viewport" className={`flex-1 overflow-y-auto px-10 py-8 sz-docs-scrollbar scroll-smooth ${isDark ? "bg-[#0f0f23]" : ""}`}>
                <div className="max-w-[1100px] mx-auto">
                  {/* Breadcrumb */}
                  <div className={`flex items-center gap-1.5 text-[13px] mb-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    <span className={`cursor-pointer ${isDark ? "hover:text-gray-300" : "hover:text-gray-600"}`} onClick={() => setSelectedCategoryName(null)}>Admin Guide</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className={`font-normal ${isDark ? "text-gray-200" : "text-gray-800"}`}>{selectedCategory.name}</span>
                  </div>

                  <h1 className={`text-[34px] font-bold tracking-tight leading-tight mb-2 ${isDark ? "text-gray-100" : "text-gray-800"}`}>
                    {selectedCategory.name}
                  </h1>
                  <p className={`text-[16px] leading-relaxed mb-10 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {selectedCategory.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    {selectedCategory.modules.map((key) => {
                      const doc = moduleDocsData[key];
                      if (!doc) return null;
                      const route = moduleRoutes[key];
                      const colorMap: Record<string, string> = {
                        blue: isDark ? "border-blue-800/40 hover:border-blue-500/60" : "border-blue-100 hover:border-blue-300",
                        violet: isDark ? "border-violet-800/40 hover:border-violet-500/60" : "border-violet-100 hover:border-violet-300",
                        indigo: isDark ? "border-indigo-800/40 hover:border-indigo-500/60" : "border-indigo-100 hover:border-indigo-300",
                        amber: isDark ? "border-amber-800/40 hover:border-amber-500/60" : "border-amber-100 hover:border-amber-300",
                        emerald: isDark ? "border-emerald-800/40 hover:border-emerald-500/60" : "border-emerald-100 hover:border-emerald-300",
                        rose: isDark ? "border-rose-800/40 hover:border-rose-500/60" : "border-rose-100 hover:border-rose-300",
                        slate: isDark ? "border-slate-700/40 hover:border-slate-500/60" : "border-slate-200 hover:border-slate-300",
                      };
                      const badgeMap: Record<string, string> = {
                        blue: isDark ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-600",
                        violet: isDark ? "bg-violet-900/30 text-violet-400" : "bg-violet-50 text-violet-600",
                        indigo: isDark ? "bg-indigo-900/30 text-indigo-400" : "bg-indigo-50 text-indigo-600",
                        amber: isDark ? "bg-amber-900/30 text-amber-400" : "bg-amber-50 text-amber-600",
                        emerald: isDark ? "bg-emerald-900/30 text-emerald-400" : "bg-emerald-50 text-emerald-600",
                        rose: isDark ? "bg-rose-900/30 text-rose-400" : "bg-rose-50 text-rose-600",
                        slate: isDark ? "bg-slate-800/40 text-slate-400" : "bg-slate-50 text-slate-600",
                      };
                      const borderClass = colorMap[selectedCategory.color] || colorMap.slate;
                      const badgeClass = badgeMap[selectedCategory.color] || badgeMap.slate;

                      return (
                        <button
                          key={key}
                          onClick={() => navigate(`/docs/${key}`)}
                          className={`text-left p-5 rounded-lg border-2 transition-all duration-200 cursor-pointer group ${borderClass} ${isDark ? "bg-[#13132b] hover:bg-[#1a1a35]" : "bg-white hover:bg-gray-50/50"}`}
                        >
                          <div className="flex items-center gap-2.5 mb-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${badgeClass}`}>
                              {doc.badge}
                            </span>
                          </div>
                          <div className={`text-[15px] font-semibold mb-1 group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {doc.title}
                          </div>
                          <div className={`text-[12px] leading-relaxed mb-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                            {doc.description}
                          </div>
                          {route && (
                            <div className={`text-[11px] font-medium ${isDark ? "text-[#696cff]/70" : "text-[#696cff]/60"}`}>
                              View documentation &rarr;
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="pb-20" />
                </div>
              </div>
            ) : (
              /* ── Feature Modules: Landing Page with Category Cards ── */
              <div id="docs-inner-viewport" className={`flex-1 overflow-y-auto sz-docs-scrollbar scroll-smooth ${isDark ? "bg-[#0f0f23]" : "bg-gray-50/30"}`}>
                {/* Hero Section */}
                <div className={`relative overflow-hidden ${isDark ? "bg-gradient-to-br from-[#13132b] via-[#1a1a40] to-[#0f0f23]" : "bg-gradient-to-br from-white via-indigo-50/40 to-white"}`}>
                  <div className="sz-landing-mesh-1" />
                  <div className="sz-landing-mesh-2" />
                  <div className="relative z-10 max-w-[1100px] mx-auto px-10 pt-12 pb-10">
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide ${isDark ? "bg-[#696cff]/15 text-[#8b8eff] border border-[#696cff]/20" : "bg-[#696cff]/8 text-[#696cff] border border-[#696cff]/15"}`}>
                        <Sparkles className="w-3 h-3" />
                        ADMIN DOCUMENTATION
                      </div>
                    </div>
                    <h1 className={`text-[42px] font-extrabold tracking-tight leading-[1.1] mb-3 ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                      <span className="sz-landing-gradient-text">Skillzen</span> Admin Guide
                    </h1>
                    <p className={`text-[17px] leading-relaxed max-w-[600px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Comprehensive documentation for every module in the Skillzen admin panel. Select a category below or use the sidebar to navigate.
                    </p>

                    {/* Stats Bar */}
                    <div className={`flex items-center gap-6 mt-8 pt-6 border-t ${isDark ? "border-[#1e1e3a]" : "border-gray-200/60"}`}>
                      {[
                        { label: "Categories", value: moduleCategories.length },
                        { label: "Modules", value: moduleCategories.reduce((a, c) => a + c.modules.length, 0) },
                        { label: "Guides", value: "Admin + Learner" },
                      ].map((stat) => (
                        <div key={stat.label} className="flex items-center gap-2.5">
                          <span className={`text-[20px] font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>{stat.value}</span>
                          <span className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}>{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Category Cards Grid */}
                <div className="max-w-[1100px] mx-auto px-10 py-8">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {moduleCategories.map((category) => {
                      const IconComp = categoryIconMap[category.icon] || BookOpen;
                      const iconBgMap: Record<string, string> = {
                        blue: isDark ? "bg-blue-500/10 text-blue-400" : "bg-blue-50 text-blue-500",
                        violet: isDark ? "bg-violet-500/10 text-violet-400" : "bg-violet-50 text-violet-500",
                        indigo: isDark ? "bg-indigo-500/10 text-indigo-400" : "bg-indigo-50 text-indigo-500",
                        amber: isDark ? "bg-amber-500/10 text-amber-400" : "bg-amber-50 text-amber-500",
                        emerald: isDark ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-50 text-emerald-500",
                        rose: isDark ? "bg-rose-500/10 text-rose-400" : "bg-rose-50 text-rose-500",
                        slate: isDark ? "bg-slate-500/10 text-slate-400" : "bg-slate-100 text-slate-500",
                        red: isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-500",
                      };
                      const iconBg = iconBgMap[category.color] || iconBgMap.slate;

                      return (
                        <button
                          key={category.name}
                          onClick={() => {
                            if (category.modules.length === 1) {
                              navigate(`/docs/${category.modules[0]}`);
                            } else {
                              setSelectedCategoryName(category.name);
                            }
                          }}
                          className={`sz-landing-card text-left p-5 rounded-xl transition-all duration-300 cursor-pointer group border ${
                            isDark
                              ? "bg-[#13132b]/80 hover:bg-[#1a1a40] border-[#1e1e3a] hover:border-[#696cff]/30"
                              : "bg-white hover:bg-white border-gray-200/80 hover:border-[#696cff]/30 hover:shadow-lg hover:shadow-indigo-500/5"
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 ${iconBg}`}>
                              <IconComp className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className={`text-[14px] font-semibold mb-0.5 group-hover:text-[#696cff] transition-colors ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                                {category.name}
                              </div>
                              <div className={`text-[12px] leading-relaxed line-clamp-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                                {category.description}
                              </div>
                              <div className={`flex items-center gap-1.5 mt-2.5 text-[11px] font-medium ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isDark ? "bg-[#1e1e3a] text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                                  {category.modules.length}
                                </span>
                                <span>{category.modules.length === 1 ? "module" : "modules"}</span>
                                <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200" />
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="pb-20" />
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      <style>{`
        .sz-docs-scrollbar::-webkit-scrollbar { width: 5px; }
        .sz-docs-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .sz-docs-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
        .sz-docs-scrollbar::-webkit-scrollbar-thumb:hover { background: #d1d5db; }

        /* ─── Landing Page Styles ─── */

        .sz-landing-gradient-text {
          background: linear-gradient(135deg, #696cff 0%, #8b8eff 50%, #a78bfa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .sz-landing-mesh-1 {
          position: absolute;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.15;
          background: radial-gradient(circle, #696cff 0%, transparent 70%);
          top: -100px;
          right: -50px;
          animation: sz-mesh-float 8s ease-in-out infinite;
          pointer-events: none;
        }

        .sz-landing-mesh-2 {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.1;
          background: radial-gradient(circle, #a78bfa 0%, transparent 70%);
          bottom: -80px;
          left: 10%;
          animation: sz-mesh-float 8s ease-in-out infinite reverse;
          pointer-events: none;
        }

        @keyframes sz-mesh-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(15px, -10px) scale(1.08); }
        }

        .sz-landing-card {
          position: relative;
          transform: translateY(0);
        }

        .sz-landing-card:hover {
          transform: translateY(-2px);
        }

        /* ─── Screenshots ─── */

        .sz-docs-content .sz-screenshot,
        .sz-overview-content .sz-screenshot {
          margin: 1rem 0 1.25rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .sz-docs-content .sz-screenshot img,
        .sz-overview-content .sz-screenshot img {
          width: 100%;
          height: auto;
          display: block;
        }

        /* ─── Typography ─── */

        .sz-docs-content h2 {
          font-size: 1.125rem;
          font-weight: 650;
          color: #111827;
          margin-top: 0;
          margin-bottom: 0.75rem;
          letter-spacing: -0.01em;
        }

        .sz-docs-content > div > section + section { margin-top: 0; }

        .sz-docs-content section[id] {
          scroll-margin-top: 1.5rem;
        }

        .sz-docs-content .sz-callout-tip[id] {
          scroll-margin-top: 1.5rem;
        }

        .sz-docs-content p {
          font-size: 0.875rem;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 0.75rem;
        }

        .sz-docs-content p:last-child { margin-bottom: 0; }

        /* ─── Tables ─── */

        .sz-docs-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.5rem 0 0;
          font-size: 0.8125rem;
        }

        .sz-docs-content thead th {
          text-align: left;
          padding: 0.5rem 0.875rem;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
        }

        .sz-docs-content tbody td {
          padding: 0.625rem 0.875rem;
          color: #374151;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: top;
          font-size: 0.8125rem;
          line-height: 1.5;
        }

        .sz-docs-content tbody tr:last-child td { border-bottom: none; }
        .sz-docs-content tbody tr:hover td { background: #fafbfc; }

        /* ─── Inline code ─── */

        .sz-docs-content code {
          background: #f3f4f6;
          color: #1f2937;
          padding: 0.1rem 0.35rem;
          border-radius: 3px;
          font-size: 0.8rem;
          font-weight: 550;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
        }

        /* ─── Steps (timeline) ─── */

        .sz-docs-content .sz-steps {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin: 0.75rem 0 0.25rem;
          border-left: 2px solid #e5e7eb;
          margin-left: 11px;
        }

        .sz-docs-content .sz-step {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 0.75rem 0 0.75rem 1.25rem;
          position: relative;
        }

        .sz-docs-content .sz-step-num {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #f3f4f6;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: absolute;
          left: -13px;
          top: 0.75rem;
          border: 2px solid #fff;
        }

        .sz-docs-content .sz-step-num.sz-step-done {
          background: #111827;
          color: #fff;
        }

        .sz-docs-content .sz-step > div:last-child {
          padding-left: 0.5rem;
        }

        .sz-docs-content .sz-step-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 0.125rem;
          line-height: 1.4;
        }

        .sz-docs-content .sz-step-desc {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 0;
        }

        /* ─── Callouts ─── */

        .sz-docs-content .sz-callout {
          padding: 0.875rem 1rem;
          border-radius: 6px;
          font-size: 0.8125rem;
          line-height: 1.7;
          color: #374151;
        }

        .sz-docs-content .sz-callout-info {
          background: #f5f3ff;
          border-left: 3px solid #696cff;
        }

        .sz-docs-content .sz-callout-tip {
          background: #f8fafc;
          border-left: 3px solid #f59e0b;
        }

        .sz-docs-content .sz-callout-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #92400e;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 0.375rem;
        }

        .sz-docs-content .sz-callout ul {
          margin: 0;
          padding-left: 1rem;
          list-style: disc;
        }

        .sz-docs-content .sz-callout li {
          font-size: 0.8125rem;
          color: #4b5563;
          line-height: 1.7;
          margin-bottom: 0.125rem;
        }

        /* ─── Bullet lists ─── */

        .sz-docs-content .sz-list {
          margin: 0.5rem 0 0;
          padding-left: 1.125rem;
          list-style: disc;
        }

        .sz-docs-content .sz-list li {
          font-size: 0.8125rem;
          color: #4b5563;
          line-height: 1.75;
          margin-bottom: 0.375rem;
        }

        .sz-docs-content .sz-list li strong {
          color: #111827;
          font-weight: 600;
        }

        /* ─── Use cases ─── */

        .sz-docs-content .sz-use-cases {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .sz-docs-content .sz-use-case {
          padding: 1rem 1.125rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .sz-docs-content .sz-use-case-title {
          font-size: 0.8125rem;
          font-weight: 650;
          color: #111827;
          margin-bottom: 0.25rem !important;
        }

        .sz-docs-content .sz-use-case-flow {
          font-size: 0.75rem;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          color: #9ca3af;
          margin-bottom: 0.5rem !important;
          letter-spacing: -0.01em;
        }

        .sz-docs-content .sz-use-case p:last-child {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-bottom: 0;
        }

        /* ─── Code blocks ─── */

        .sz-docs-content .sz-code-block {
          margin: 0.75rem 0 0.25rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        .sz-docs-content .sz-code-header {
          background: #f3f4f6;
          padding: 0.375rem 0.875rem;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #6b7280;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          letter-spacing: 0.02em;
          border-bottom: 1px solid #e5e7eb;
        }

        .sz-docs-content .sz-code-block pre {
          margin: 0;
          padding: 1rem;
          background: #1f2937;
          overflow-x: auto;
        }

        .sz-docs-content .sz-code-block pre code {
          background: none;
          padding: 0;
          border-radius: 0;
          color: #d1d5db;
          font-size: 0.8rem;
          line-height: 1.7;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          white-space: pre;
        }

        /* ─── Method badges ─── */

        .sz-docs-content .sz-method-list {
          display: flex;
          gap: 0.375rem;
          flex-wrap: wrap;
          margin-top: 0.25rem;
        }

        .sz-docs-content .sz-method-list code {
          font-size: 0.6875rem;
          font-weight: 700;
          padding: 0.25rem 0.625rem;
          border-radius: 4px;
          background: #f3f4f6;
          color: #374151;
          letter-spacing: 0.02em;
        }

        /* ─── Overview-specific styles ─── */

        .sz-overview-content h3 {
          font-size: 0.9375rem;
          font-weight: 650;
          color: #1f2937;
          margin-top: 1.75rem;
          margin-bottom: 0.5rem;
        }

        .sz-overview-content p {
          font-size: 0.875rem;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 0.75rem;
        }

        .sz-overview-content p:last-child { margin-bottom: 0; }

        .sz-overview-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.5rem 0 0;
          font-size: 0.8125rem;
        }

        .sz-overview-content thead th {
          text-align: left;
          padding: 0.5rem 0.875rem;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
        }

        .sz-overview-content tbody td {
          padding: 0.625rem 0.875rem;
          color: #374151;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: top;
          font-size: 0.8125rem;
          line-height: 1.5;
        }

        .sz-overview-content tbody tr:last-child td { border-bottom: none; }
        .sz-overview-content tbody tr:hover td { background: #fafbfc; }

        .sz-overview-content code {
          background: #f3f4f6;
          color: #1f2937;
          padding: 0.1rem 0.35rem;
          border-radius: 3px;
          font-size: 0.8rem;
          font-weight: 550;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
        }

        .sz-overview-content .sz-callout {
          padding: 0.875rem 1rem;
          border-radius: 6px;
          font-size: 0.8125rem;
          line-height: 1.7;
          color: #374151;
        }

        .sz-overview-content .sz-callout-info {
          background: #f5f3ff;
          border-left: 3px solid #696cff;
        }

        .sz-overview-content .sz-callout-tip {
          background: #f8fafc;
          border-left: 3px solid #f59e0b;
        }

        .sz-overview-content .sz-callout-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #92400e;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 0.375rem;
        }

        .sz-overview-content .sz-callout ul {
          margin: 0;
          padding-left: 1rem;
          list-style: disc;
        }

        .sz-overview-content .sz-callout li {
          font-size: 0.8125rem;
          color: #4b5563;
          line-height: 1.7;
          margin-bottom: 0.125rem;
        }

        .sz-overview-content .sz-list {
          margin: 0.5rem 0 0;
          padding-left: 1.125rem;
          list-style: disc;
        }

        .sz-overview-content .sz-list li {
          font-size: 0.8125rem;
          color: #4b5563;
          line-height: 1.75;
          margin-bottom: 0.375rem;
        }

        .sz-overview-content .sz-list li strong {
          color: #111827;
          font-weight: 600;
        }

        .sz-overview-content .sz-steps {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin: 0.75rem 0 0.25rem;
          border-left: 2px solid #e5e7eb;
          margin-left: 11px;
        }

        .sz-overview-content .sz-step {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 0.75rem 0 0.75rem 1.25rem;
          position: relative;
        }

        .sz-overview-content .sz-step-num {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #f3f4f6;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: absolute;
          left: -13px;
          top: 0.75rem;
          border: 2px solid #fff;
        }

        .sz-overview-content .sz-step-num.sz-step-done {
          background: #111827;
          color: #fff;
        }

        .sz-overview-content .sz-step > div:last-child {
          padding-left: 0.5rem;
        }

        .sz-overview-content .sz-step-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 0.125rem;
          line-height: 1.4;
        }

        .sz-overview-content .sz-step-desc {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 0;
        }

        /* ─── Pipeline visual ─── */

        .sz-docs-content .sz-pipeline-visual,
        .sz-overview-content .sz-pipeline-visual {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          margin: 1rem 0 1.5rem;
          padding: 1.5rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 8px;
        }

        .sz-docs-content .sz-pipeline-node,
        .sz-overview-content .sz-pipeline-node {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
          padding: 0.5rem 1.25rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: white;
          text-align: center;
          min-width: 180px;
        }

        .sz-docs-content .sz-pipeline-trigger,
        .sz-overview-content .sz-pipeline-trigger {
          border-color: #fca5a5;
          background: #fef2f2;
          color: #991b1b;
        }

        .sz-docs-content .sz-pipeline-action,
        .sz-overview-content .sz-pipeline-action {
          border-color: #e5e7eb;
          background: white;
        }

        .sz-docs-content .sz-pipeline-condition,
        .sz-overview-content .sz-pipeline-condition {
          border-color: #c4b5fd;
          background: #f5f3ff;
          color: #5b21b6;
        }

        .sz-docs-content .sz-pipeline-export,
        .sz-overview-content .sz-pipeline-export {
          border-color: #6ee7b7;
          background: #ecfdf5;
          color: #065f46;
        }

        .sz-docs-content .sz-pipeline-arrow,
        .sz-overview-content .sz-pipeline-arrow {
          font-size: 0.875rem;
          color: #9ca3af;
          padding: 0.125rem 0;
        }

        .sz-docs-content .sz-pipeline-branch,
        .sz-overview-content .sz-pipeline-branch {
          display: flex;
          gap: 2rem;
          margin: 0.25rem 0;
          padding: 0.75rem 1rem;
          position: relative;
        }

        .sz-docs-content .sz-pipeline-branch-yes,
        .sz-docs-content .sz-pipeline-branch-no,
        .sz-overview-content .sz-pipeline-branch-yes,
        .sz-overview-content .sz-pipeline-branch-no {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          flex: 1;
        }

        .sz-docs-content .sz-pipeline-branch-label,
        .sz-overview-content .sz-pipeline-branch-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #9ca3af;
          margin-bottom: 0.5rem;
          background: #f3f4f6;
          padding: 0.125rem 0.5rem;
          border-radius: 3px;
        }

        /* ─── Execution patterns ─── */

        .sz-docs-content .sz-patterns,
        .sz-overview-content .sz-patterns {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin: 0.75rem 0 0.5rem;
        }

        .sz-docs-content .sz-pattern,
        .sz-overview-content .sz-pattern {
          padding: 1rem 1.125rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .sz-docs-content .sz-pattern-label,
        .sz-overview-content .sz-pattern-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .sz-docs-content .sz-pattern-flow,
        .sz-overview-content .sz-pattern-flow {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
        }

        .sz-docs-content .sz-pattern-flow span,
        .sz-overview-content .sz-pattern-flow span {
          background: #f3f4f6;
          padding: 0.2rem 0.5rem;
          border-radius: 3px;
          font-size: 0.8125rem;
        }

        .sz-docs-content .sz-pattern-arrow,
        .sz-overview-content .sz-pattern-arrow {
          color: #9ca3af !important;
          background: none !important;
          padding: 0 !important;
        }

        .sz-docs-content .sz-pattern p,
        .sz-overview-content .sz-pattern p {
          margin-bottom: 0;
        }

        /* ─── Module categories ─── */

        .sz-docs-content .sz-module-cats,
        .sz-overview-content .sz-module-cats {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin: 0.75rem 0 0.5rem;
        }

        .sz-docs-content .sz-module-cat,
        .sz-overview-content .sz-module-cat {
          padding: 1rem 1.25rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .sz-docs-content .sz-module-cat-header,
        .sz-overview-content .sz-module-cat-header {
          font-size: 0.8125rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.375rem;
          padding-left: 0.75rem;
          border-left: 3px solid #e5e7eb;
        }

        .sz-docs-content .sz-module-cat p,
        .sz-overview-content .sz-module-cat p {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-bottom: 0.375rem;
        }

        .sz-docs-content .sz-module-cat .sz-list,
        .sz-overview-content .sz-module-cat .sz-list {
          margin-top: 0.25rem;
        }

        /* ─── Architecture layers ─── */

        .sz-docs-content .sz-arch-layers,
        .sz-overview-content .sz-arch-layers {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          margin: 1rem 0 1.5rem;
        }

        .sz-docs-content .sz-arch-layer,
        .sz-overview-content .sz-arch-layer {
          width: 100%;
          padding: 1rem 1.25rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .sz-docs-content .sz-arch-presentation,
        .sz-overview-content .sz-arch-presentation { background: #fefce8; border-color: #fde68a; }
        .sz-docs-content .sz-arch-engine,
        .sz-overview-content .sz-arch-engine { background: #eff6ff; border-color: #bfdbfe; }
        .sz-docs-content .sz-arch-integration,
        .sz-overview-content .sz-arch-integration { background: #f0fdf4; border-color: #bbf7d0; }

        .sz-docs-content .sz-arch-layer-label,
        .sz-overview-content .sz-arch-layer-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .sz-docs-content .sz-arch-layer-items,
        .sz-overview-content .sz-arch-layer-items {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .sz-docs-content .sz-arch-layer-items span,
        .sz-overview-content .sz-arch-layer-items span {
          font-size: 0.8125rem;
          font-weight: 500;
          color: #374151;
          background: rgba(255,255,255,0.7);
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.06);
        }

        .sz-docs-content .sz-arch-connector,
        .sz-overview-content .sz-arch-connector {
          font-size: 1rem;
          color: #9ca3af;
          padding: 0.25rem 0;
          text-align: center;
        }

        /* ─── Example split ─── */

        .sz-docs-content .sz-example-split,
        .sz-overview-content .sz-example-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin: 1rem 0 1.5rem;
        }

        @media (max-width: 768px) {
          .sz-docs-content .sz-example-split,
          .sz-overview-content .sz-example-split {
            grid-template-columns: 1fr;
          }
        }

        .sz-docs-content .sz-example-block,
        .sz-overview-content .sz-example-block {
          padding: 1.25rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .sz-docs-content .sz-example-user,
        .sz-overview-content .sz-example-user { background: #fefce8; border-color: #fde68a; }
        .sz-docs-content .sz-example-engine,
        .sz-overview-content .sz-example-engine { background: #f0f9ff; border-color: #bae6fd; }

        .sz-docs-content .sz-example-label,
        .sz-overview-content .sz-example-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6b7280;
          margin-bottom: 0.75rem;
        }

        .sz-docs-content .sz-example-flow,
        .sz-overview-content .sz-example-flow {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          flex-wrap: wrap;
          margin-bottom: 0.75rem;
        }

        .sz-docs-content .sz-example-node,
        .sz-overview-content .sz-example-node {
          font-size: 0.75rem;
          font-weight: 600;
          color: #374151;
          background: rgba(255,255,255,0.8);
          padding: 0.3rem 0.625rem;
          border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.08);
          white-space: nowrap;
        }

        .sz-docs-content .sz-example-arrow,
        .sz-overview-content .sz-example-arrow {
          font-size: 0.875rem;
          color: #9ca3af;
        }

        .sz-docs-content .sz-example-caption,
        .sz-overview-content .sz-example-caption {
          font-size: 0.75rem !important;
          color: #9ca3af !important;
          font-style: italic;
          margin-bottom: 0 !important;
        }

        /* ─── Code block in overview ─── */

        .sz-overview-content .sz-code-block {
          margin: 0.75rem 0 0.25rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        .sz-overview-content .sz-code-header {
          background: #f3f4f6;
          padding: 0.375rem 0.875rem;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #6b7280;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          letter-spacing: 0.02em;
          border-bottom: 1px solid #e5e7eb;
        }

        .sz-overview-content .sz-code-block pre {
          margin: 0;
          padding: 1rem;
          background: #1f2937;
          overflow-x: auto;
        }

        .sz-overview-content .sz-code-block pre code {
          background: none;
          padding: 0;
          border-radius: 0;
          color: #d1d5db;
          font-size: 0.8rem;
          line-height: 1.7;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          white-space: pre;
        }

        .sz-overview-content .sz-method-list {
          display: flex;
          gap: 0.375rem;
          flex-wrap: wrap;
          margin-top: 0.25rem;
        }

        .sz-overview-content .sz-method-list code {
          font-size: 0.6875rem;
          font-weight: 700;
          padding: 0.25rem 0.625rem;
          border-radius: 4px;
          background: #f3f4f6;
          color: #374151;
          letter-spacing: 0.02em;
        }

        .sz-overview-content .sz-screenshot {
          margin: 1rem 0 1.25rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .sz-overview-content .sz-screenshot img {
          width: 100%;
          height: auto;
          display: block;
        }

        .sz-overview-content .sz-use-cases {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .sz-overview-content .sz-use-case {
          padding: 1rem 1.125rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .sz-overview-content .sz-use-case-title {
          font-size: 0.8125rem;
          font-weight: 650;
          color: #111827;
          margin-bottom: 0.25rem !important;
        }

        .sz-overview-content .sz-use-case-flow {
          font-size: 0.75rem;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          color: #9ca3af;
          margin-bottom: 0.5rem !important;
          letter-spacing: -0.01em;
        }

        .sz-overview-content .sz-use-case p:last-child {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-bottom: 0;
        }
        /* ─── Dark mode: scrollbar ─── */

        .sz-dark .sz-docs-scrollbar::-webkit-scrollbar-thumb { background: #2a2a4a; }
        .sz-dark .sz-docs-scrollbar::-webkit-scrollbar-thumb:hover { background: #3a3a5a; }

        /* ─── Dark mode: screenshots ─── */

        .sz-dark .sz-docs-content .sz-screenshot,
        .sz-dark .sz-overview-content .sz-screenshot {
          border-color: #1e1e3a;
          background: #1a1a35;
          box-shadow: 0 4px 24px rgba(0,0,0,0.3);
        }

        .sz-dark .sz-docs-content .sz-screenshot img,
        .sz-dark .sz-overview-content .sz-screenshot img {
          opacity: 0.92;
          transition: opacity 0.2s;
        }

        .sz-dark .sz-docs-content .sz-screenshot:hover img,
        .sz-dark .sz-overview-content .sz-screenshot:hover img {
          opacity: 1;
        }

        /* ─── Dark mode: typography ─── */

        .sz-dark .sz-docs-content h2,
        .sz-dark .sz-overview-content h3 { color: #e5e7eb; }

        .sz-dark .sz-docs-content p,
        .sz-dark .sz-overview-content p { color: #9ca3af; }

        /* ─── Dark mode: tables ─── */

        .sz-dark .sz-docs-content thead th,
        .sz-dark .sz-overview-content thead th {
          color: #6b7280;
          border-bottom-color: #1e1e3a;
        }

        .sz-dark .sz-docs-content tbody td,
        .sz-dark .sz-overview-content tbody td {
          color: #d1d5db;
          border-bottom-color: #1a1a35;
        }

        .sz-dark .sz-docs-content tbody tr:hover td,
        .sz-dark .sz-overview-content tbody tr:hover td { background: #1a1a35; }

        /* ─── Dark mode: inline code ─── */

        .sz-dark .sz-docs-content code,
        .sz-dark .sz-overview-content code {
          background: #1a1a35;
          color: #c4b5fd;
        }

        /* ─── Dark mode: steps ─── */

        .sz-dark .sz-docs-content .sz-steps,
        .sz-dark .sz-overview-content .sz-steps { border-left-color: #2a2a4a; }

        .sz-dark .sz-docs-content .sz-step-num,
        .sz-dark .sz-overview-content .sz-step-num {
          background: #1a1a35;
          color: #9ca3af;
          border-color: #0f0f23;
        }

        .sz-dark .sz-docs-content .sz-step-num.sz-step-done,
        .sz-dark .sz-overview-content .sz-step-num.sz-step-done {
          background: #696cff;
          color: #fff;
        }

        .sz-dark .sz-docs-content .sz-step-title,
        .sz-dark .sz-overview-content .sz-step-title { color: #e5e7eb; }

        .sz-dark .sz-docs-content .sz-step-desc,
        .sz-dark .sz-overview-content .sz-step-desc { color: #9ca3af; }

        /* ─── Dark mode: callouts ─── */

        .sz-dark .sz-docs-content .sz-callout,
        .sz-dark .sz-overview-content .sz-callout { color: #d1d5db; }

        .sz-dark .sz-docs-content .sz-callout-info,
        .sz-dark .sz-overview-content .sz-callout-info {
          background: rgba(105,108,255,0.08);
          border-left-color: #696cff;
        }

        .sz-dark .sz-docs-content .sz-callout-tip,
        .sz-dark .sz-overview-content .sz-callout-tip {
          background: rgba(245,158,11,0.08);
          border-left-color: #f59e0b;
        }

        .sz-dark .sz-docs-content .sz-callout-label,
        .sz-dark .sz-overview-content .sz-callout-label { color: #f59e0b; }

        .sz-dark .sz-docs-content .sz-callout li,
        .sz-dark .sz-overview-content .sz-callout li { color: #9ca3af; }

        /* ─── Dark mode: bullet lists ─── */

        .sz-dark .sz-docs-content .sz-list li,
        .sz-dark .sz-overview-content .sz-list li { color: #9ca3af; }

        .sz-dark .sz-docs-content .sz-list li strong,
        .sz-dark .sz-overview-content .sz-list li strong { color: #e5e7eb; }

        /* ─── Dark mode: use cases ─── */

        .sz-dark .sz-docs-content .sz-use-case,
        .sz-dark .sz-overview-content .sz-use-case {
          background: #1a1a35;
          border-color: #2a2a4a;
        }

        .sz-dark .sz-docs-content .sz-use-case-title,
        .sz-dark .sz-overview-content .sz-use-case-title { color: #e5e7eb; }

        .sz-dark .sz-docs-content .sz-use-case p:last-child,
        .sz-dark .sz-overview-content .sz-use-case p:last-child { color: #9ca3af; }

        /* ─── Dark mode: code blocks ─── */

        .sz-dark .sz-docs-content .sz-code-block,
        .sz-dark .sz-overview-content .sz-code-block {
          border-color: #2a2a4a;
        }

        .sz-dark .sz-docs-content .sz-code-header,
        .sz-dark .sz-overview-content .sz-code-header {
          background: #1a1a35;
          color: #9ca3af;
          border-bottom-color: #2a2a4a;
        }

        .sz-dark .sz-docs-content .sz-code-block pre,
        .sz-dark .sz-overview-content .sz-code-block pre { background: #0a0a1a; }

        .sz-dark .sz-docs-content .sz-code-block pre code,
        .sz-dark .sz-overview-content .sz-code-block pre code { color: #c4b5fd; }

        /* ─── Dark mode: method badges ─── */

        .sz-dark .sz-docs-content .sz-method-list code,
        .sz-dark .sz-overview-content .sz-method-list code {
          background: #1a1a35;
          color: #c4b5fd;
        }

        /* ─── Dark mode: pipeline visual ─── */

        .sz-dark .sz-docs-content .sz-pipeline-visual,
        .sz-dark .sz-overview-content .sz-pipeline-visual {
          background: #1a1a35;
          border-color: #2a2a4a;
        }

        .sz-dark .sz-docs-content .sz-pipeline-node,
        .sz-dark .sz-overview-content .sz-pipeline-node {
          color: #d1d5db;
          border-color: #2a2a4a;
          background: #13132b;
        }

        .sz-dark .sz-docs-content .sz-pipeline-trigger,
        .sz-dark .sz-overview-content .sz-pipeline-trigger { background: rgba(239,68,68,0.1); border-color: #7f1d1d; color: #fca5a5; }

        .sz-dark .sz-docs-content .sz-pipeline-condition,
        .sz-dark .sz-overview-content .sz-pipeline-condition { background: rgba(105,108,255,0.1); border-color: #4c1d95; color: #c4b5fd; }

        .sz-dark .sz-docs-content .sz-pipeline-export,
        .sz-dark .sz-overview-content .sz-pipeline-export { background: rgba(16,185,129,0.1); border-color: #065f46; color: #6ee7b7; }

        .sz-dark .sz-docs-content .sz-pipeline-branch-label,
        .sz-dark .sz-overview-content .sz-pipeline-branch-label { background: #1a1a35; color: #6b7280; }

        /* ─── Dark mode: patterns ─── */

        .sz-dark .sz-docs-content .sz-pattern,
        .sz-dark .sz-overview-content .sz-pattern {
          background: #1a1a35;
          border-color: #2a2a4a;
        }

        .sz-dark .sz-docs-content .sz-pattern-flow,
        .sz-dark .sz-overview-content .sz-pattern-flow { color: #d1d5db; }

        .sz-dark .sz-docs-content .sz-pattern-flow span,
        .sz-dark .sz-overview-content .sz-pattern-flow span { background: #13132b; }

        /* ─── Dark mode: module categories ─── */

        .sz-dark .sz-docs-content .sz-module-cat,
        .sz-dark .sz-overview-content .sz-module-cat {
          background: #1a1a35;
          border-color: #2a2a4a;
        }

        .sz-dark .sz-docs-content .sz-module-cat-header,
        .sz-dark .sz-overview-content .sz-module-cat-header {
          color: #e5e7eb;
          border-left-color: #696cff;
        }

        /* ─── Dark mode: architecture layers ─── */

        .sz-dark .sz-docs-content .sz-arch-layer,
        .sz-dark .sz-overview-content .sz-arch-layer { border-color: #2a2a4a; }

        .sz-dark .sz-docs-content .sz-arch-presentation,
        .sz-dark .sz-overview-content .sz-arch-presentation { background: rgba(253,224,71,0.06); }
        .sz-dark .sz-docs-content .sz-arch-engine,
        .sz-dark .sz-overview-content .sz-arch-engine { background: rgba(96,165,250,0.06); }
        .sz-dark .sz-docs-content .sz-arch-integration,
        .sz-dark .sz-overview-content .sz-arch-integration { background: rgba(74,222,128,0.06); }

        .sz-dark .sz-docs-content .sz-arch-layer-items span,
        .sz-dark .sz-overview-content .sz-arch-layer-items span {
          color: #d1d5db;
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.08);
        }

        /* ─── Dark mode: example blocks ─── */

        .sz-dark .sz-docs-content .sz-example-block,
        .sz-dark .sz-overview-content .sz-example-block { border-color: #2a2a4a; }

        .sz-dark .sz-docs-content .sz-example-user,
        .sz-dark .sz-overview-content .sz-example-user { background: rgba(253,224,71,0.06); border-color: #422006; }
        .sz-dark .sz-docs-content .sz-example-engine,
        .sz-dark .sz-overview-content .sz-example-engine { background: rgba(96,165,250,0.06); border-color: #1e3a5f; }

        .sz-dark .sz-docs-content .sz-example-node,
        .sz-dark .sz-overview-content .sz-example-node {
          color: #d1d5db;
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.08);
        }

        /* ─── Enhanced screenshot hover (both modes) ─── */

        .sz-docs-content .sz-screenshot,
        .sz-overview-content .sz-screenshot {
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }

        .sz-docs-content .sz-screenshot:hover,
        .sz-overview-content .sz-screenshot:hover {
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          transform: translateY(-1px);
        }

        .sz-dark .sz-docs-content .sz-screenshot:hover,
        .sz-dark .sz-overview-content .sz-screenshot:hover {
          box-shadow: 0 8px 30px rgba(0,0,0,0.5);
        }
      `}</style>

      {/* ─── BACK TO TOP BUTTON ─── */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 cursor-pointer ${
            isDark
              ? "bg-[#696cff] text-white hover:bg-[#5a5de6] shadow-[0_4px_20px_rgba(105,108,255,0.4)]"
              : "bg-[#696cff] text-white hover:bg-[#5a5de6] shadow-[0_4px_20px_rgba(105,108,255,0.3)]"
          }`}
          title="Back to top"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default SkillzenDocs;
